import React, { useEffect, useRef, useState } from 'react';
import ToolTipWrapper from 'app/components/ToolTipWrapper/ToolTipWrapper';

/** Shared hover hold before showing portaled Spectrum tooltips (side nav, date chip, etc.). */
export const TOOLTIP_HOVER_DELAY_MS = 1500;

interface DelayedTooltipProps {
  message: string | React.ReactNode;
  children: React.ReactElement;
  placement?: 'top' | 'bottom' | 'left' | 'right';
  inlineFlex?: boolean;
  cursor?: 'help' | 'pointer' | 'default';
  ariaLabel?: string;
}

/** Tooltip that opens after {@link TOOLTIP_HOVER_DELAY_MS} and closes immediately on mouse out. */
export const DelayedTooltip: React.FC<DelayedTooltipProps> = ({
  message,
  children,
  placement,
  inlineFlex,
  cursor = 'help',
  ariaLabel
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const hoverTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleMouseEnter = () => {
    if (hoverTimerRef.current) {
      clearTimeout(hoverTimerRef.current);
    }
    hoverTimerRef.current = setTimeout(() => {
      setIsOpen(true);
    }, TOOLTIP_HOVER_DELAY_MS);
  };

  const handleMouseLeave = () => {
    if (hoverTimerRef.current) {
      clearTimeout(hoverTimerRef.current);
      hoverTimerRef.current = null;
    }
    setIsOpen(false);
  };

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      setIsOpen(false);
    }
  };

  useEffect(
    () => () => {
      if (hoverTimerRef.current) {
        clearTimeout(hoverTimerRef.current);
      }
    },
    []
  );

  return (
    <ToolTipWrapper
      message={message}
      placement={placement}
      inlineFlex={inlineFlex}
      cursor={cursor}
      ariaLabel={ariaLabel}
      isOpen={isOpen}
      onOpenChange={handleOpenChange}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {children}
    </ToolTipWrapper>
  );
};
